import {
    Alert, Box, Button, Chip, Divider, Paper, Stack, Switch, Tab, Tabs,
    TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAddPlayer, useAdjust, useAdminUsers, useAudit, useCloseHangout,
    useCreateHangoutAdmin, useCreateUser, useOverview, useUpdateUser,
} from '../hooks/useAdmin';
import { useStandings } from '../hooks/useHangout';
import { formatTokens } from '../lib/format';
import { HandleSchema, PinSchema } from '../schemas/auth.schema';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';

export function Admin() {
    const session = useAuthStore((s) => s.session);
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);

    // The API answers 404 for non-admins, so this only avoids a pointless
    // round trip -- it is not the access control.
    if (session && !session.isAdmin) {
        return (
            <Stack spacing={2}>
                <Typography variant="h2">Not for you</Typography>
                <Button onClick={() => navigate('/')} variant="contained">
                    Back to the board
                </Button>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="overline" sx={{ color: colors.safety }}>
                    Commissioner
                </Typography>
                <Typography variant="h2">Admin</Typography>
            </Box>

            <Tabs
                value={tab}
                onChange={(_event, value: number) => setTab(value)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ borderBottom: `1px solid ${colors.line}` }}
            >
                <Tab label="Hangout" />
                <Tab label="People" />
                <Tab label="Tokens" />
                <Tab label="Audit" />
            </Tabs>

            {tab === 0 && <HangoutPanel />}
            {tab === 1 && <PeoplePanel />}
            {tab === 2 && <TokensPanel />}
            {tab === 3 && <AuditPanel />}
        </Stack>
    );
}

function HangoutPanel() {
    const overview = useOverview();
    const create = useCreateHangoutAdmin();
    const close = useCloseHangout();
    const users = useAdminUsers();
    const addPlayer = useAddPlayer();
    const standings = useStandings(overview.data?.current?.id);

    const [name, setName] = useState('');
    const [venue, setVenue] = useState('');
    const [stake, setStake] = useState('10000');
    const [maxStake, setMaxStake] = useState('1000');
    const [confirmClose, setConfirmClose] = useState(false);

    const current = overview.data?.current ?? null;
    const inHangout = new Set((standings.data ?? []).map((row) => row.userId));

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
                <Stat label="Players" value={overview.data?.users ?? 0} />
                <Stat label="Hangouts" value={overview.data?.hangouts ?? 0} />
                <Stat label="Bets" value={overview.data?.markets ?? 0} />
            </Stack>

            {current ? (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="overline" sx={{ color: colors.safety }}>
                        Open — anyone can join
                    </Typography>
                    <Typography variant="h3" sx={{ textTransform: 'none' }}>
                        {current.name}
                    </Typography>
                    {current.venue && (
                        <Typography color="text.secondary">{current.venue}</Typography>
                    )}
                    <Typography variant="body2" sx={{ color: colors.muted, mt: 0.5 }}>
                        {current.startingStake.toLocaleString('en-US')} each · up to{' '}
                        {current.maxStakePerMarket.toLocaleString('en-US')} on any one bet
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="overline" sx={{ color: colors.muted }}>
                        Who&rsquo;s in
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {(users.data ?? []).map((user) => (
                            <Chip
                                key={user.id}
                                label={user.handle}
                                onClick={
                                    inHangout.has(user.id)
                                        ? undefined
                                        : () =>
                                              addPlayer.mutate({
                                                  hangoutId: current.id,
                                                  userId: user.id,
                                              })
                                }
                                sx={{
                                    bgcolor: inHangout.has(user.id) ? colors.win : colors.raised,
                                    color: inHangout.has(user.id) ? colors.ground : colors.ink,
                                }}
                            />
                        ))}
                    </Stack>
                    <Typography variant="body2" sx={{ color: colors.muted, mt: 1 }}>
                        Everyone is added automatically. Tap anyone still grey to pull them in
                        by hand.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {close.isError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {getErrorMessage(close.error)}
                        </Alert>
                    )}
                    {close.isSuccess && (
                        <Alert severity="success" sx={{ mb: 1 }}>
                            Closed. Standings are frozen.
                        </Alert>
                    )}

                    <Typography variant="body2" sx={{ color: colors.muted, mb: 1 }}>
                        Only one hangout runs at a time — close this one to start another.
                    </Typography>

                    {confirmClose ? (
                        <Stack spacing={1}>
                            <Alert severity="warning">
                                Closing voids every unsettled bet, refunds those stakes, and
                                freezes the final standings. It can&rsquo;t be undone.
                            </Alert>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    color="error"
                                    variant="contained"
                                    disabled={close.isPending}
                                    onClick={() => close.mutate(current.id)}
                                >
                                    {close.isPending ? 'Closing…' : 'Yes, close it'}
                                </Button>
                                <Button color="inherit" onClick={() => setConfirmClose(false)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Button
                            color="error"
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={() => setConfirmClose(true)}
                        >
                            Close out the night
                        </Button>
                    )}
                </Paper>
            ) : (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="overline" sx={{ color: colors.muted }}>
                        Nothing running
                    </Typography>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <TextField
                            label="What is it"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Kyle's, Saturday"
                        />
                        <TextField
                            label="Where (optional)"
                            value={venue}
                            onChange={(event) => setVenue(event.target.value)}
                        />
                        <TextField
                            label="Everyone starts on"
                            value={stake}
                            onChange={(event) => setStake(event.target.value.replace(/\D/g, ''))}
                            type="tel"
                            inputMode="numeric"
                        />
                        <TextField
                            label="Most anyone can put on one bet"
                            value={maxStake}
                            onChange={(event) => setMaxStake(event.target.value.replace(/\D/g, ''))}
                            type="tel"
                            inputMode="numeric"
                            helperText="Stops one person making a pot nobody else wants to join"
                        />
                        {create.isError && (
                            <Alert severity="error">{getErrorMessage(create.error)}</Alert>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            disabled={name.trim().length === 0 || create.isPending}
                            onClick={() =>
                                create.mutate({
                                    name: name.trim(),
                                    venue: venue.trim() || undefined,
                                    startingStake: Number(stake) || 10_000,
                                    maxStakePerMarket: Number(maxStake) || undefined,
                                })
                            }
                        >
                            {create.isPending ? 'Opening…' : 'Open it'}
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Stack>
    );
}

function PeoplePanel() {
    const users = useAdminUsers();
    const create = useCreateUser();
    const update = useUpdateUser();

    const [handle, setHandle] = useState('');
    const [pin, setPin] = useState('');
    const [editing, setEditing] = useState<number | null>(null);
    const [newPin, setNewPin] = useState('');
    const [newHandle, setNewHandle] = useState('');

    const handleOk = HandleSchema.safeParse(handle).success;
    const pinOk = PinSchema.safeParse(pin).success;

    return (
        <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    Add somebody
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                        label="Name"
                        value={handle}
                        onChange={(event) => setHandle(event.target.value)}
                        helperText="Spaces are fine"
                    />
                    <TextField
                        label="Their PIN"
                        value={pin}
                        onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
                        type="tel"
                        inputMode="numeric"
                        helperText="Tell them what it is; they can't sign in without one"
                    />
                    {create.isError && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
                    <Button
                        variant="contained"
                        disabled={!handleOk || !pinOk || create.isPending}
                        onClick={() =>
                            create.mutate(
                                { handle: handle.trim(), pin },
                                { onSuccess: () => { setHandle(''); setPin(''); } },
                            )
                        }
                    >
                        {create.isPending ? 'Adding…' : 'Add them'}
                    </Button>
                </Stack>
            </Paper>

            {(users.data ?? []).map((user) => (
                <Paper key={user.id} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>{user.handle}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                {!user.hasPin && (
                                    <Chip size="small" label="No PIN — can't sign in" sx={{ bgcolor: colors.raised, color: colors.loss }} />
                                )}
                                {user.locked && (
                                    <Chip size="small" label="Locked out" sx={{ bgcolor: colors.raised, color: colors.loss }} />
                                )}
                                {user.isAdmin && (
                                    <Chip size="small" label="Commissioner" sx={{ bgcolor: colors.raised, color: colors.safety }} />
                                )}
                            </Stack>
                        </Box>
                        <Switch
                            checked={user.isAdmin}
                            onChange={(event) =>
                                update.mutate({ id: user.id, patch: { isAdmin: event.target.checked } })
                            }
                            inputProps={{ 'aria-label': `Commissioner access for ${user.handle}` }}
                        />
                    </Stack>

                    {editing === user.id ? (
                        <Stack spacing={1} sx={{ mt: 1.5 }}>
                            <TextField
                                label="Rename"
                                value={newHandle}
                                onChange={(event) => setNewHandle(event.target.value)}
                                size="small"
                            />
                            <TextField
                                label="Set a new PIN"
                                value={newPin}
                                onChange={(event) => setNewPin(event.target.value.replace(/\D/g, ''))}
                                size="small"
                                type="tel"
                                inputMode="numeric"
                            />
                            {update.isError && (
                                <Alert severity="error">{getErrorMessage(update.error)}</Alert>
                            )}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={update.isPending}
                                    onClick={() =>
                                        update.mutate(
                                            {
                                                id: user.id,
                                                patch: {
                                                    handle: newHandle.trim() || undefined,
                                                    pin: newPin || undefined,
                                                    unlock: true,
                                                },
                                            },
                                            { onSuccess: () => setEditing(null) },
                                        )
                                    }
                                >
                                    Save
                                </Button>
                                <Button size="small" color="inherit" onClick={() => setEditing(null)}>
                                    Cancel
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button
                                size="small"
                                color="inherit"
                                variant="outlined"
                                onClick={() => {
                                    setEditing(user.id);
                                    setNewHandle(user.handle);
                                    setNewPin('');
                                }}
                            >
                                Edit
                            </Button>
                            {user.locked && (
                                <Button
                                    size="small"
                                    color="inherit"
                                    variant="outlined"
                                    onClick={() => update.mutate({ id: user.id, patch: { unlock: true } })}
                                >
                                    Unlock
                                </Button>
                            )}
                        </Stack>
                    )}
                </Paper>
            ))}
        </Stack>
    );
}

function TokensPanel() {
    const overview = useOverview();
    const standings = useStandings(overview.data?.current?.id);
    const adjust = useAdjust();

    const [target, setTarget] = useState<number | null>(null);
    const [delta, setDelta] = useState('');
    const [reason, setReason] = useState('');

    const hangoutId = overview.data?.current?.id;
    const amount = Number(delta);
    const ready =
        target !== null && Number.isInteger(amount) && amount !== 0 && reason.trim().length >= 3;

    if (!hangoutId) {
        return <Typography color="text.secondary">No hangout is running.</Typography>;
    }

    return (
        <Stack spacing={2}>
            <Alert severity="info">
                An adjustment appends a ledger entry beside whatever it corrects. Nothing is ever
                edited or removed, so the history stays readable.
            </Alert>

            <Paper sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    Who
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5, mb: 2 }}>
                    {(standings.data ?? []).map((row) => (
                        <Chip
                            key={row.userId}
                            label={`${row.handle} · ${formatTokens(row.balance)}`}
                            onClick={() => setTarget(row.userId)}
                            sx={{
                                bgcolor: target === row.userId ? colors.safety : colors.raised,
                                color: target === row.userId ? colors.ground : colors.ink,
                            }}
                        />
                    ))}
                </Stack>

                <Stack spacing={1.5}>
                    <TextField
                        label="Change by"
                        value={delta}
                        onChange={(event) => setDelta(event.target.value.replace(/[^\d-]/g, ''))}
                        type="tel"
                        helperText="Negative takes tokens away"
                    />
                    <TextField
                        label="Why"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        helperText="Recorded in the audit log and visible to everyone"
                    />
                    {adjust.isError && <Alert severity="error">{getErrorMessage(adjust.error)}</Alert>}
                    {adjust.isSuccess && <Alert severity="success">Posted.</Alert>}
                    <Button
                        variant="contained"
                        disabled={!ready || adjust.isPending}
                        onClick={() =>
                            adjust.mutate(
                                {
                                    hangoutId,
                                    userId: target as number,
                                    delta: amount,
                                    reason: reason.trim(),
                                },
                                { onSuccess: () => { setDelta(''); setReason(''); } },
                            )
                        }
                    >
                        {adjust.isPending ? 'Posting…' : 'Post the adjustment'}
                    </Button>
                </Stack>
            </Paper>
        </Stack>
    );
}

function AuditPanel() {
    const audit = useAudit();

    if (audit.data?.length === 0) {
        return <Typography color="text.secondary">Nothing has needed intervening in yet.</Typography>;
    }

    return (
        <Stack spacing={1}>
            {(audit.data ?? []).map((row) => (
                <Paper key={row.id} sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontFamily: mono, fontSize: '0.7rem', color: colors.safety, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            {row.kind.replace(/_/g, ' ')}
                        </Typography>
                        <Typography sx={{ fontFamily: mono, fontSize: '0.68rem', color: colors.muted }}>
                            {row.createdAt.toLocaleString()}
                        </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.5 }}>{row.reason}</Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: '0.68rem', color: colors.muted, mt: 0.5 }}>
                        {row.actorHandle} → {row.target}
                    </Typography>
                </Paper>
            ))}
        </Stack>
    );
}

function Stat({ label, value }: { label: string; value: number }) {
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="overline" sx={{ color: colors.muted }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: mono, fontSize: '1.5rem', fontWeight: 600, lineHeight: 1 }}>
                {value}
            </Typography>
        </Box>
    );
}
